import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase'; // Ajustado
import { useAuth } from '../contexts/AuthContext'; // Ajustado
import { getAllDefinedBadges, getUserProfileBadges, updateHighlightedBadges, type UserProfileBadge } from '../lib/gamification/badgeService'; // Ajustado
import type { Database } from '../types/supabase'; // Ajustado
import { Button } from '../components/ui/button'; // Ajustado
import { Checkbox } from '../components/ui/checkbox'; // Ajustado
import { toast } from 'react-hot-toast';
import { Loader2, Star, Check, HelpCircle, Lock } from 'lucide-react';

type BadgeRow = Database['public']['Tables']['badges']['Row'];

const MyBadgesPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [allBadges, setAllBadges] = useState<BadgeRow[]>([]);
  const [userBadgesMap, setUserBadgesMap] = useState<Map<string, UserProfileBadge>>(new Map());
  const [selectedHighlightIds, setSelectedHighlightIds] = useState<Set<string>>(new Set());

  const fetchBadges = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [definedBadges, profileBadges] = await Promise.all([
        getAllDefinedBadges(supabase),
        getUserProfileBadges(supabase, user.id)
      ]);

      setAllBadges(definedBadges);

      const map = new Map<string, UserProfileBadge>();
      const highlighted = new Set<string>();
      profileBadges.forEach(b => {
        map.set(b.id, b);
        if (b.is_highlighted) {
          highlighted.add(b.id);
        }
      });
      setUserBadgesMap(map);
      setSelectedHighlightIds(highlighted);

    } catch (error) {
      console.error("Error fetching badges:", error);
      toast.error("Falha ao carregar badges.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  const handleHighlightToggle = (badgeId: string, isOwned: boolean) => {
    if (!isOwned) {
        toast.error("Você só pode destacar badges que já possui.");
        return;
    }
    setSelectedHighlightIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(badgeId)) {
        newSet.delete(badgeId);
      } else {
        if (newSet.size < 5) {
          newSet.add(badgeId);
        } else {
          toast.error("Você pode selecionar no máximo 5 badges para destacar.");
        }
      }
      return newSet;
    });
  };

  const handleSaveChanges = async () => {
      if (!user) return;
      setSaving(true);
      const result = await updateHighlightedBadges(supabase, user.id, Array.from(selectedHighlightIds));
      setSaving(false);
      if (!result.success) {
          toast.error(result.error || 'Erro desconhecido ao salvar.');
      }
  };

  const ownedBadges = useMemo(() => {
      return allBadges.filter(b => userBadgesMap.has(b.id)).map(b => ({
          ...b,
          ...userBadgesMap.get(b.id) // Adiciona unlocked_at e is_highlighted
      }));
  }, [allBadges, userBadgesMap]);

  const availableBadges = useMemo(() => {
      return allBadges.filter(b => !userBadgesMap.has(b.id));
  }, [allBadges, userBadgesMap]);


  const renderBadgeCard = (badge: BadgeRow | UserProfileBadge, isOwned: boolean) => {
    const userBadgeData = isOwned ? userBadgesMap.get(badge.id) : undefined;
    const isHighlighted = selectedHighlightIds.has(badge.id);

    return (
      <div key={badge.id} className={`border rounded-lg p-4 flex flex-col items-center space-y-2 relative ${isOwned ? 'bg-card' : 'bg-muted/40 opacity-70'}`}>
        <img src={badge.icon_url || 'https://via.placeholder.com/64'} alt={badge.name} className="w-16 h-16 mb-2"/>
        <h3 className="font-semibold text-center">{badge.name}</h3>
        <p className="text-sm text-muted-foreground text-center">{badge.description || 'Sem descrição.'}</p>

        {isOwned && userBadgeData?.unlocked_at && (
           <p className="text-xs text-green-600">Desbloqueado em: {new Date(userBadgeData.unlocked_at).toLocaleDateString()}</p>
        )}

        {!isOwned && !badge.is_hidden && (
            <div className="text-xs text-amber-600 mt-2 text-center">
                <p className="italic">Critérios não atendidos</p>
            </div>
        )}

        {!isOwned && badge.is_hidden && (
            <div className="text-xs text-purple-600 mt-2 flex items-center justify-center space-x-1">
                <Lock size={12} />
                <span>Badge Secreta</span>
            </div>
        )}

        {/* Controles */}
        <div className="flex items-center justify-center space-x-2 pt-2 mt-auto">
            {isOwned && (
                <>
                    <Checkbox
                        id={`highlight-${badge.id}`}
                        checked={isHighlighted}
                        onCheckedChange={() => handleHighlightToggle(badge.id, true)}
                        disabled={saving || (!isHighlighted && selectedHighlightIds.size >= 5)}
                    />
                    <label htmlFor={`highlight-${badge.id}`} className="text-xs cursor-pointer">Destacar</label>
                </>
            )}
            {!isOwned && !badge.is_hidden && (
                 <span className="text-xs text-muted-foreground">Bloqueado</span>
            )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="container mx-auto p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Minhas Badges</h1>
        <Button onClick={handleSaveChanges} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
          Salvar Destaques
        </Button>
      </div>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 border-b pb-2">Badges Desbloqueadas ({ownedBadges.length})</h2>
        {ownedBadges.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {ownedBadges.map(badge => renderBadgeCard(badge, true))}
            </div>
        ) : (
            <p className="text-muted-foreground">Você ainda não desbloqueou nenhuma badge. Explore a plataforma!</p>
        )}
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-4 border-b pb-2">Badges Disponíveis ({availableBadges.length})</h2>
         {availableBadges.length > 0 ? (
             <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {availableBadges.map(badge => renderBadgeCard(badge, false))}
             </div>
         ) : (
             <p className="text-muted-foreground">Parabéns! Você coletou todas as badges disponíveis no momento.</p>
         )}

      </section>
       <p className="text-sm text-muted-foreground mt-8">
           Selecione até 5 badges desbloqueadas para destacá-las em seu perfil público.
       </p>
    </div>
  );
};

export default MyBadgesPage; 